import * as THREE from 'three';
import { tagStroke } from '../../utils/neuralSceneUtils';
import {
  collectNodeDiskScreens,
  labelHitsOccupied,
  latchYield,
  pickParentLabelSeat,
  wantsPersistentName,
  placeLabelAtAngle,
  placeLabelUnderNode,
  setLabelCameraBasis,
} from './labelSlots';

export function stepNodeLabels({
  labelByTaskId,
  meshByTaskId,
  camera,
  container,
  now,
  selectedTaskIdLocal,
  MIN_DIST,
  MAX_DIST,
  worldPos,
  projectForCard,
  placeLabelNearMesh,
  mountLabel,
  freezeSet = false,
  extraBoxes = null,
  hoverPointerId = null,
  manualClear = true,
}) {
  const width = Math.max(container.clientWidth, 1);
  const height = Math.max(container.clientHeight, 1);
  const basisOk = setLabelCameraBasis(camera);

  if (freezeSet) {
    labelByTaskId.forEach((rec, tid) => {
      const mesh = meshByTaskId.get(tid);
      if (!rec.label?.parent || !mesh?.visible || mesh.userData.exiting) return;
      if (basisOk) {
        const depth = mesh.userData.nodeMeta?.nestDepth ?? 0;
        if (depth === 0) {
          placeLabelAtAngle(
            camera, mesh, rec.label, rec.element, width, height,
            rec.seatAngle ?? 0, rec.seatReach ?? 1,
          );
        } else {
          placeLabelUnderNode(camera, mesh, rec.label, rec.element, width, height);
        }
      } else placeLabelNearMesh(mesh, rec.label);
    });
    return;
  }

  const labelCandidates = [];
  labelByTaskId.forEach((rec, tid) => {
    const { element: el } = rec;
    const mesh = meshByTaskId.get(tid);
    if (!mesh?.visible || mesh.userData.exiting || tid === selectedTaskIdLocal) return;
    const meta = mesh.userData.nodeMeta;
    worldPos.copy(mesh.position);
    worldPos.y -= 0.16;
    const distCam = camera.position.distanceTo(worldPos);
    const nearT = 1 - THREE.MathUtils.smoothstep(distCam, MIN_DIST * 1.05, MAX_DIST * 0.55);
    const lit = mesh.userData.ignitionUntil > now;
    const isSatellite = !!meta?.parentTaskId;
    const depth = meta?.nestDepth ?? 0;
    const inProgress = meta?.statusCategory === 'in_progress';
    const live = !!(meta?.isAvailable || inProgress);
    const forced = lit;
    if (!wantsPersistentName(meta, forced)) return;
    if (isSatellite && nearT < 0.55 && !forced) return;
    if (nearT < 0.45 && !live && !forced) return;
    projectForCard.copy(worldPos).project(camera);
    if (Math.abs(projectForCard.x) > 0.97 || Math.abs(projectForCard.y) > 0.92 || projectForCard.z > 1) return;
    const size = meta?.size ?? 1;
    const layerDim = isSatellite && !forced ? 0.78 : 1;
    const opacity = (lit ? 1 : live ? 0.72 + nearT * 0.28 : 0.4 + nearT * 0.35) * mesh.userData.visT * layerDim;
    labelCandidates.push({
      tid, rec, mesh, el, lit, opacity, depth,
      score: (lit ? 10 : 0) + (inProgress ? 5 : 0) + (meta?.isAvailable ? 4 : 0) + size * 0.25 - (isSatellite ? 1.5 : 0),
    });
  });

  const titleBoxes = extraBoxes ? extraBoxes.filter(Boolean).map((box) => ({ ...box })) : [];
  const placed = [];
  const parentDisks = basisOk ? collectNodeDiskScreens(meshByTaskId, camera, width, height) : [];
  const placedIds = new Set();
  labelCandidates.sort((a, b) => (
    a.depth - b.depth || b.score - a.score || (a.tid < b.tid ? -1 : a.tid > b.tid ? 1 : 0)
  )).forEach((label) => {
    if (label.depth > 0 && placedIds.size >= 14) return;
    let box = null;
    if (basisOk && label.depth === 0) {
      const seat = pickParentLabelSeat(
        camera, label.mesh, label.rec, label.el, label.tid, width, height, placed, parentDisks,
        manualClear,
      );
      box = seat.box;
      label.rec.seatAngle = seat.angle;
      label.rec.seatReach = seat.reachMul;
      label.rec.yielded = false;
      label.rec.yieldHold = 0;
    } else {
      box = basisOk
        ? placeLabelUnderNode(camera, label.mesh, label.rec.label, label.el, width, height)
        : null;
      if (!box) placeLabelNearMesh(label.mesh, label.rec.label);
      const blocked = labelHitsOccupied(
        box, placed.concat(titleBoxes), parentDisks, label.depth, label.tid, !!label.rec.yielded, manualClear,
      );
      if (latchYield(label.rec, blocked)) return;
    }
    mountLabel(label.rec, true);
    label.el.style.opacity = String(label.opacity);
    label.el.style.pointerEvents = 'auto';
    label.el.style.fontSize = '12px';
    const stroke = tagStroke(label.mesh.userData.baseColor, 0.58);
    label.el.style.borderColor = stroke;
    label.el.classList.toggle('neural-view__node-tag--ignited', label.lit);
    label.el.classList.toggle('neural-view__node-tag--hovered', label.tid === hoverPointerId);
    placed.push(box || { left: 0, top: 0, w: 150, h: 26 });
    placedIds.add(label.tid);
  });
  labelByTaskId.forEach((rec, tid) => {
    if (placedIds.has(tid)) return;
    rec.element.style.opacity = '0';
    rec.element.style.pointerEvents = 'none';
    rec.element.classList.remove('neural-view__node-tag--hovered');
    mountLabel(rec, false);
  });
}
